import { DollarSign, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  useCreateNoteMutation,
  useDeleteNoteMutation,
  useGetNotesQuery,
  useUpdateNoteMutation,
} from '@/services/api/matterNotesService';
import {
  BaseMatterNote,
  DamageNote,
  NOTE_TYPES,
} from '@/services/types/matter-notes-types';

import { MatterSectionCard } from './MatterSectionCard';

interface DamagesCardProps {
  matterId: string;
}

interface DamageRowProps {
  damage: DamageNote;
  matterId: string;
  onDeleted: () => void;
}

const DamageRow = ({ damage, matterId, onDeleted }: DamageRowProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState(damage.description || '');
  const [amountStr, setAmountStr] = useState(
    damage.amount != null ? String(damage.amount) : '',
  );

  const [updateNote, { isLoading: isUpdating }] = useUpdateNoteMutation();
  const [deleteNote, { isLoading: isDeleting }] = useDeleteNoteMutation();

  const handleSave = async () => {
    const parsedAmount = amountStr ? parseFloat(amountStr) : null;
    try {
      await updateNote({
        matterId,
        noteId: damage.id,
        noteType: NOTE_TYPES.DAMAGE,
        note: { description, amount: parsedAmount } as any,
      }).unwrap();
      toast.success('Damage updated');
      setIsEditing(false);
    } catch {
      toast.error('Failed to update damage');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this damage entry?')) {
      // eslint-disable-line no-alert
      return;
    }

    try {
      await deleteNote({
        matterId,
        noteId: damage.id,
        noteType: NOTE_TYPES.DAMAGE,
      }).unwrap();
      toast.success('Damage deleted');
      onDeleted();
    } catch {
      toast.error('Failed to delete damage');
    }
  };

  const handleCancel = () => {
    setDescription(damage.description || '');
    setAmountStr(damage.amount != null ? String(damage.amount) : '');
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="p-4 bg-zinc-50 rounded-lg space-y-3">
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Damage description..."
          rows={2}
          className="text-sm"
        />
        <Input
          value={amountStr}
          onChange={(e) => setAmountStr(e.target.value)}
          placeholder="Amount (e.g., 5000)"
          type="number"
          className="text-sm"
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isUpdating}>
            {isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between py-3 px-4 bg-zinc-50 rounded-lg group">
      <div
        className="flex-1 min-w-0 cursor-pointer"
        onClick={() => setIsEditing(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsEditing(true);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <p className="text-sm font-medium text-zinc-900">
          {damage.description || 'No description'}
        </p>
        {damage.amount != null && (
          <p className="text-xs font-medium text-zinc-500 mt-0.5">
            ${damage.amount.toLocaleString()}
          </p>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDelete}
        disabled={isDeleting}
        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-600"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

interface NewDamageFormProps {
  matterId: string;
  onSaved: () => void;
  onCancelled: () => void;
}

const NewDamageForm = ({
  matterId,
  onSaved,
  onCancelled,
}: NewDamageFormProps) => {
  const [description, setDescription] = useState('');
  const [amountStr, setAmountStr] = useState('');
  const [createNote, { isLoading }] = useCreateNoteMutation();

  const handleSave = async () => {
    if (!description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    const parsedAmount = amountStr ? parseFloat(amountStr) : null;
    try {
      await createNote({
        matterId,
        noteType: NOTE_TYPES.DAMAGE,
        note: { description, amount: parsedAmount } as any,
      }).unwrap();
      toast.success('Damage added');
      onSaved();
    } catch {
      toast.error('Failed to add damage');
    }
  };

  return (
    <div className="p-4 bg-zinc-50 rounded-lg space-y-3 border-l-4 border-primary">
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Damage description..."
        rows={2}
        className="text-sm"
        autoFocus
      />
      <Input
        value={amountStr}
        onChange={(e) => setAmountStr(e.target.value)}
        placeholder="Amount (e.g., 5000)"
        type="number"
        className="text-sm"
      />
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancelled}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Adding...' : 'Add'}
        </Button>
      </div>
    </div>
  );
};

export const DamagesCard = ({ matterId }: DamagesCardProps) => {
  const [showNewForm, setShowNewForm] = useState(false);

  const {
    data: damages,
    isLoading,
    refetch,
  } = useGetNotesQuery({
    matterId,
    noteType: NOTE_TYPES.DAMAGE,
  });

  const handleAddNew = () => {
    setShowNewForm(true);
  };

  const handleSaved = () => {
    setShowNewForm(false);
    refetch();
  };

  const handleDeleted = () => {
    refetch();
  };

  return (
    <MatterSectionCard
      title="Damages"
      icon={<DollarSign className="h-4 w-4" />}
      isLoading={isLoading}
      headerActions={
        !showNewForm ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleAddNew}
            className="h-7 px-2 text-xs text-primary hover:text-primary/80"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-2">
        {showNewForm && (
          <NewDamageForm
            matterId={matterId}
            onSaved={handleSaved}
            onCancelled={() => setShowNewForm(false)}
          />
        )}

        {(damages as BaseMatterNote[] | undefined)?.length ? (
          (damages as DamageNote[]).map((damage) => (
            <DamageRow
              key={damage.id}
              damage={damage}
              matterId={matterId}
              onDeleted={handleDeleted}
            />
          ))
        ) : !showNewForm ? (
          <p className="text-sm font-medium text-zinc-500">
            No damages recorded
          </p>
        ) : null}
      </div>
    </MatterSectionCard>
  );
};
